/**
 * Suggested-reply markers let the assistant offer quick, clickable replies
 * (e.g. "Approve | Reject | Tell me more") without the user having to type.
 *
 * The assistant emits an HTML comment at the end of its message:
 *
 *   <!-- suggest: Approve the plan | Adjust the approach | Scope it down -->
 *
 * This logic is platform-agnostic and lives in `@maestro/core` so every surface
 * parses and strips the marker identically:
 * - The web UI (react-markdown, configured without rehype-raw) never renders the
 *   comment, but strips it as defense-in-depth and turns the labels into buttons.
 * - The Telegram adapter strips the marker from the outgoing text and renders the
 *   labels as an inline keyboard, so the raw comment never leaks as visible text.
 *
 * This module has NO imports by design: it must stay safe to import from the
 * browser bundle (via `@maestro/core/suggested-replies`) without pulling in any
 * Node-only code from the core barrel.
 */

/** First complete marker; capture group 1 is the raw, pipe-separated label list. */
const PARSE_MARKER_RE = /<!--\s*suggest:\s*([^]*?)\s*-->/i;
/** Every complete marker (for stripping). */
const STRIP_COMPLETE_RE = /<!--\s*suggest:[^]*?-->/gi;
/** An unterminated trailing marker — e.g. while the message is still streaming. */
const STRIP_UNTERMINATED_RE = /<!--\s*suggest:[^]*$/i;

/** Maximum number of suggested-reply labels surfaced from a single marker. */
export const MAX_SUGGESTED_REPLIES = 6;
/** Maximum character length of an individual suggested-reply label. */
export const MAX_SUGGESTED_REPLY_LABEL_LENGTH = 80;

/**
 * Extract suggested-reply labels from the first marker in `content`.
 * Trims each label, drops empties and labels over
 * {@link MAX_SUGGESTED_REPLY_LABEL_LENGTH}, deduplicates, and caps the count at
 * {@link MAX_SUGGESTED_REPLIES}. Returns `[]` when there is no marker or no
 * usable labels.
 */
export function parseSuggestedReplies(content: string): string[] {
  const match = PARSE_MARKER_RE.exec(content);
  if (!match) return [];

  const seen = new Set<string>();
  const replies: string[] = [];
  for (const segment of match[1].split('|')) {
    const label = segment.trim();
    if (!label || label.length > MAX_SUGGESTED_REPLY_LABEL_LENGTH) continue;
    if (seen.has(label)) continue;
    seen.add(label);
    replies.push(label);
    if (replies.length >= MAX_SUGGESTED_REPLIES) break;
  }
  return replies;
}

/**
 * Whether `content` carries a suggested-reply marker at all — a complete
 * `<!-- suggest: ... -->` or an unterminated trailing one (mid-stream). This is
 * a strict superset of "has usable labels": a marker whose labels are all empty
 * or over {@link MAX_SUGGESTED_REPLY_LABEL_LENGTH} still counts as present, so
 * callers can strip it even when {@link parseSuggestedReplies} returns `[]`.
 *
 * Reuses the same regexes as {@link stripSuggestedRepliesMarker} — exactly the
 * markers that function removes — so "present" and "stripped" never diverge.
 */
export function hasSuggestedRepliesMarker(content: string): boolean {
  STRIP_COMPLETE_RE.lastIndex = 0;
  return STRIP_COMPLETE_RE.test(content) || STRIP_UNTERMINATED_RE.test(content);
}

/**
 * Remove suggested-reply markers (complete or an unterminated trailing one)
 * from `content` so they never appear in the rendered message or a copy of it.
 * Leaves unrelated HTML comments intact.
 */
export function stripSuggestedRepliesMarker(content: string): string {
  return content.replace(STRIP_COMPLETE_RE, '').replace(STRIP_UNTERMINATED_RE, '').trimEnd();
}
